import { IInventoryPurchaseOrderProductSearchResponseData } from "@/api_framework/api_modals/Inventory";
import { SectionSearchDataTypes } from "@/app/hooks/api_hooks/emr/useEMRSectionSearchAPI";
import { useInventoryProductSearchAPI } from "@/app/hooks/api_hooks/Inventory/PO/useInventoryProductSearchAPI";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useDebounce } from "@uidotdev/usehooks";
import clsx from "clsx";
import { debounce } from "lodash";
import { Fragment, useEffect, useState } from "react";

export type EMRSectionSearchResultType = {
  name: string;
  id: string;
};

const AuditTrailProductSearchComponent: React.FC<{
  searchType: SectionSearchDataTypes;
  flexibleAdd: boolean;
  showSelected?: boolean;
  containerClassName?: string;
  placeholder?: string;
  inputContainerClassName?: string;
  itemActiveClassName?: string;
  variant_category_id?: string; // This is passed as a prop
  isInputEnabled?: boolean;
  onSelectItem?: (item: IInventoryPurchaseOrderProductSearchResponseData) => void;
}> = ({
  searchType,
  containerClassName,
  inputContainerClassName,
  onSelectItem,
  variant_category_id,
  showSelected,
  ...props
}) => {
  // Destructure variant_category_id here
  const { searchResults, execute: searchInSection, clearSearchResults } = useInventoryProductSearchAPI();
  const [selected, setSelected] = useState<IInventoryPurchaseOrderProductSearchResponseData | null>(null);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 300);

  const debounceSearch = debounce(() => {
    // if (variant_category_id) {
    searchInSection(debouncedQuery, variant_category_id);
    // }
  }, 500);

  useEffect(() => {
    if (debouncedQuery.length >= 3) {
      debounceSearch();
    }
  }, [debouncedQuery, variant_category_id]);

  const reducedSearchResults = searchResults?.reduce((final, current) => {
    final.push({
      name: current?.variant_name,
      id: current?.vetic_variant_id,
    });
    return final;
  }, [] as EMRSectionSearchResultType[]);

  return (
    <div className={clsx(containerClassName)}>
      <Combobox
        value={selected}
        onChange={(item) => {
          setSelected(item);
          if (onSelectItem) {
            onSelectItem(item);
          }
        }}
      >
        <div className="relative mt-1 w-full">
          <div className="relative border-gray-200 bg-white border rounded-lg focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 w-full text-left text-xs cursor-default overflow-hidden focus:outline-none">
            <div className="flex justify-start items-center">
              {query?.length >= 3 ? (
                <button
                  className="p-2"
                  onClick={() => {
                    setQuery("");
                    clearSearchResults();
                    setSelected(null);
                    if (onSelectItem) {
                      onSelectItem(null);
                    }
                  }}
                >
                  <XMarkIcon className="w-4 h-4 text-red-500" />
                </button>
              ) : (
                <span className="p-2">
                  <MagnifyingGlassIcon className="w-4 h-4 text-gray-500" />
                </span>
              )}
              <Combobox.Button className={clsx(inputContainerClassName)}>
                <Combobox.Input
                  autoComplete="off"
                  autoCorrect="off"
                  value={selected ? selected.name : query}
                  placeholder={props?.placeholder ?? "Search Item by name"}
                  className="py-2 pr-10 pl-3 border-none focus:ring-0 w-full text-gray-900 text-xs leading-5"
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSelected(null); // optional: clear selected item while typing
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Backspace" && query === "") {
                      setSelected(null);
                      if (onSelectItem) {
                        onSelectItem(null);
                      }
                    }
                  }}
                  readOnly={props?.isInputEnabled}
                />
              </Combobox.Button>
            </div>

            <Combobox.Button className="right-0 absolute inset-y-0 flex items-center pr-2">
              <ChevronUpDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
            </Combobox.Button>
          </div>
          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Combobox.Options className="z-30 absolute bg-white shadow-lg mt-1 py-1 rounded-md ring-1 ring-black/5 w-full max-h-60 text-xs overflow-auto focus:outline-none sm:text-sm">
              <div className="pl-4 w-full">
                {props.isInputEnabled ? (
                  <p className="py-3 font-medium text-red-400 text-xs">
                    Please Select Vendor Branch And Clinic Before Product Search
                  </p>
                ) : (
                  <span className="text-xs">
                    Search results for <span className="font-semibold text-blue-600">{query}</span>
                  </span>
                )}
              </div>
              {reducedSearchResults?.length === 0 && query !== "" ? (
                <div className="relative px-4 py-2 text-gray-700 cursor-default select-none">
                  <p>No Result Found.</p>
                </div>
              ) : (
                reducedSearchResults?.map((result, idx) => (
                  <Combobox.Option
                    key={`${result}:${idx}`}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                        active
                          ? props?.itemActiveClassName
                            ? props?.itemActiveClassName
                            : "bg-teal-500 text-white"
                          : "text-gray-900"
                      }`
                    }
                    value={result}
                  >
                    {({ selected, active }) => (
                      <div className="flex justify-between items-center">
                        <span
                          data-tooltip-id={`${searchType}:option`}
                          data-tooltip-content={result?.name}
                          className={`block   ${selected ? "font-medium" : "font-normal"} ${searchType === "Food" || searchType === "Medicines" ? "truncate w-72" : ""} `}
                        >
                          {result.name}
                        </span>
                        {selected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                              active ? "text-white" : "text-teal-600"
                            }`}
                          >
                            <CheckIcon className="w-5 h-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </div>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
};

export default AuditTrailProductSearchComponent;
